import React from "react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import escapeHtml from "escape-html";

// Banners uploaded through the Booking API live under this storage path.
export const isUploadedBanner = url =>
  !!url && url.includes("/online-booking/announcement/");

// Same rule as the Booking API: content images must be .jpg or .jpeg files.
export const hasAllowedImageExtension = url => {
  const path = (url ?? "").split(/[?#]/)[0].toLowerCase();
  return /\.jpe?g$/.test(path);
};

export const MAX_IMAGE_DIMENSION = 2000;
const IMAGE_LOAD_TIMEOUT = 10000;

// Loads the image to read its real pixel size (works for any host, no CORS needed).
const loadImageSize = url =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => {
      img.src = "";
      reject(new Error("timeout"));
    }, IMAGE_LOAD_TIMEOUT);
    img.onload = () => {
      clearTimeout(timer);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error("load failed"));
    };
    img.src = url;
  });

// Resolves to an error message, or null when the image is fine.
export const checkImageUrl = async url => {
  if (!hasAllowedImageExtension(url))
    return "Only .jpg images are allowed: " + url;
  try {
    const { width, height } = await loadImageSize(url);
    if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION)
      return `Image is ${width} x ${height} px, max is ${MAX_IMAGE_DIMENSION} x ${MAX_IMAGE_DIMENSION} px: ${url}`;
    return null;
  } catch (e) {
    return "Image could not be loaded: " + url;
  }
};

// Checks every <img> in the content; resolves to the list of problems.
export const checkContentImages = async html => {
  if (!html) return [];
  const body = new DOMParser().parseFromString(html, "text/html").body;
  const urls = [
    ...new Set(
      Array.from(body.querySelectorAll("img")).map(
        img => img.getAttribute("src") ?? ""
      )
    )
  ];
  const results = await Promise.all(urls.map(checkImageUrl));
  return results.filter(Boolean);
};

// Run before saving; shows the problems and resolves to false if any image fails.
export const confirmContentImages = async html => {
  const problems = await checkContentImages(html);
  if (!problems.length) return true;
  await Swal.fire({
    icon: "error",
    title: "Please fix the content images",
    html: problems.map(p => escapeHtml(p)).join("<br><br>")
  });
  return false;
};

// The Booking API returns UTC dates without the "Z" suffix.
export const fromUtc = value => (value ? new Date(value + "Z") : null);

export const announcementStatus = record => {
  if (!record.isActive) return "Inactive";
  const now = new Date();
  if (fromUtc(record.publishDate) > now) return "Scheduled";
  if (record.expiryDate && fromUtc(record.expiryDate) < now) return "Expired";
  return "Live";
};

export const emptyAnnouncement = {
  slug: "",
  title: "",
  subtitle: "",
  summary: "",
  contentHtml: "",
  publishDate: null,
  expiryDate: null,
  isActive: true
};

export const toAnnouncementInput = data => ({
  slug: data.slug ?? "",
  title: data.title ?? "",
  subtitle: data.subtitle ?? "",
  summary: data.summary ?? "",
  contentHtml: data.contentHtml ?? "",
  publishDate: fromUtc(data.publishDate),
  expiryDate: fromUtc(data.expiryDate),
  isActive: data.isActive
});

const previewStyles =
  "<style>body{font-family:Arial,Helvetica,sans-serif;color:#333;line-height:1.6;margin:16px}" +
  "table{width:100%;border-collapse:collapse}th{background:#0169AF;color:#fff}" +
  "th,td{padding:10px 14px;border-bottom:1px solid #e0e0e0;text-align:left}" +
  ".greeting,h2,h3,h4{color:#054f99}.value{color:#0169AF}.italic{font-style:italic}" +
  "img{max-width:100%;height:auto}</style>";

// Sandboxed with no permissions, so scripts in the content never run here.
export const HtmlPreview = ({ html }) => (
  <iframe
    title="Content Preview"
    sandbox=""
    srcDoc={previewStyles + (html ?? "")}
    style={{
      width: "100%",
      height: 420,
      border: "1px solid #ddd",
      borderRadius: 4,
      background: "#fff"
    }}
  />
);

HtmlPreview.propTypes = {
  html: PropTypes.string
};
